import React, { useEffect, useMemo, useState } from "react"
import API from "../utils/API"
import Sweetalert from "../utils/Sweetalert"

const AD_TYPES = [
  { id: 1, label: "Vidéos", icon: "fas fa-film" },
  { id: 2, label: "Images", icon: "far fa-image" },
  { id: 3, label: "PDF", icon: "far fa-file-pdf" },
]

const getAdIcon = (ad) => (AD_TYPES.find((type) => type.id === ad.adTypeId) || {}).icon

export default function InteractiveMenuCard({ position, menu, ads, refreshMenus, onDeleted, removeMenu }) {
  // State
  const [name, setName] = useState("")
  const [adIds, setAdIds] = useState([])
  const [saving, setSaving] = useState(false)

  // Comportements
  useEffect(() => {
    setName(menu ? menu.name : "")
    setAdIds(menu ? menu.documents.map((document) => document.adId) : [])
  }, [menu])

  const documents = useMemo(() => adIds.map((adId) => ads.find((ad) => ad.id === adId)).filter(Boolean), [ads, adIds])
  const availableAds = useMemo(() => ads.filter((ad) => !adIds.includes(ad.id)), [ads, adIds])

  const savedAdIds = menu ? menu.documents.map((document) => document.adId) : []
  const hasChanges = !menu || menu.name !== name.trim() || savedAdIds.join() !== adIds.join()
  const canSave = name.trim().length > 0 && adIds.length > 0 && hasChanges

  const addDocument = (adId) => {
    if (adId) setAdIds([...adIds, adId])
  }

  const removeDocument = (adId) => setAdIds(adIds.filter((id) => id !== adId))

  const moveDocument = (index, offset) => {
    const adIdsCopy = [...adIds]
    const [adId] = adIdsCopy.splice(index, 1)
    adIdsCopy.splice(index + offset, 0, adId)
    setAdIds(adIdsCopy)
  }

  const handleSubmit = (event) => {
    event.preventDefault()
    setSaving(true)
    API.PUT(
      "/interactive/menu/" + position,
      { name: name.trim(), adIds: adIds },
      (response) => {
        Sweetalert.fireSuccessToast(response.data.message)
        setSaving(false)
        refreshMenus()
      },
      () => setSaving(false)
    )
  }

  const handleDelete = () => {
    Sweetalert.fireDeleteConfirm("Supprimer le menu " + position + " ?", "Il ne sera plus affiché sur l'écran.", () => {
      API.DELETE("/interactive/menu/" + position, (response) => {
        Sweetalert.fireSuccessToast(response.data.message)
        onDeleted()
      })
    })
  }

  // Rendu
  return (
    <div className={"card " + (menu ? "card-primary card-outline" : "card-secondary card-outline")}>
      <div className="card-header">
        <h3 className="card-title">
          <b>Menu {position}</b>
          {!menu && <span className="text-muted"> - non enregistré</span>}
        </h3>
        {removeMenu && (
          <div className="card-tools">
            <button type="button" className="btn btn-tool" title="Retirer ce menu" onClick={removeMenu}>
              <i className="fas fa-times"></i>
            </button>
          </div>
        )}
      </div>
      <form onSubmit={handleSubmit}>
        <div className="card-body">
          <div className="form-group">
            <label>Nom du menu</label>
            <input
              type="text"
              className="form-control"
              placeholder="Ex : Plan du site"
              value={name}
              maxLength={50}
              required
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          <label>Documents affichés ({documents.length})</label>
          {documents.length > 0 ? (
            <ul className="list-group mb-2">
              {documents.map((ad, index) => (
                <li key={ad.id} className="list-group-item d-flex align-items-center py-1 px-2">
                  <span className="text-muted mr-2">{index + 1}.</span>
                  <span className="flex-grow-1" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    <i className={getAdIcon(ad)}></i> {ad.name}
                  </span>
                  <div className="btn-group btn-group-sm ml-2">
                    <button
                      type="button"
                      className="btn btn-default"
                      title="Monter"
                      disabled={index === 0}
                      onClick={() => moveDocument(index, -1)}
                    >
                      <i className="fas fa-arrow-up"></i>
                    </button>
                    <button
                      type="button"
                      className="btn btn-default"
                      title="Descendre"
                      disabled={index === documents.length - 1}
                      onClick={() => moveDocument(index, 1)}
                    >
                      <i className="fas fa-arrow-down"></i>
                    </button>
                    <button type="button" className="btn btn-default text-danger" title="Retirer" onClick={() => removeDocument(ad.id)}>
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted mb-2">Aucun document : ajoutez-en au moins un.</p>
          )}
          <select
            className="form-control"
            value=""
            disabled={availableAds.length === 0}
            onChange={(event) => addDocument(event.target.value)}
          >
            <option value="">+ Ajouter un document</option>
            {AD_TYPES.map((type) => {
              const adsOfType = availableAds.filter((ad) => ad.adTypeId === type.id)
              return (
                adsOfType.length > 0 && (
                  <optgroup key={type.id} label={type.label}>
                    {adsOfType.map((ad) => (
                      <option key={ad.id} value={ad.id}>
                        {ad.name}
                      </option>
                    ))}
                  </optgroup>
                )
              )
            })}
          </select>
        </div>
        <div className="card-footer">
          <button type="submit" className="btn btn-primary" disabled={saving || !canSave}>
            <i className="fas fa-save"></i> Enregistrer
          </button>
          {menu && (
            <button type="button" className="btn btn-outline-danger float-right" onClick={handleDelete}>
              <i className="fas fa-trash"></i> Supprimer
            </button>
          )}
        </div>
      </form>
    </div>
  )
}
